import { AppliedFilterData, FilterConfig, FilterConfigField } from './common';

function findFilterConfig(
  config: FilterConfig,
  filter: AppliedFilterData,
): FilterConfigField | undefined {
  for (const cfg of config) {
    if (cfg.type === 'separator') continue;

    if (cfg.type === 'category') {
      // For category filters, check if this is the category
      if (cfg.id === filter.category) {
        // If there's a field, find it in the fields array
        if (filter.field) {
          const fieldConfig = cfg.fields.find(
            (f) => f.type !== 'separator' && f.id === filter.field,
          );
          if (fieldConfig && fieldConfig.type !== 'separator') {
            return fieldConfig;
          }
        }
        // If no field, return the category itself
        return cfg;
      }
    } else {
      // For non-category filters, check if this is the filter
      if (cfg.id === filter.category) {
        return cfg;
      }
    }
  }

  return undefined;
}

export function filtersToExternalQuery(
  config: FilterConfig,
  filters: Array<AppliedFilterData>,
): Record<string, any> {
  const queryParams: Record<string, any> = {};

  filters.forEach((filter) => {
    const filterConfig = findFilterConfig(config, filter);

    let params: Record<string, any>;

    if (filterConfig && 'toExternalQuery' in filterConfig && filterConfig.toExternalQuery) {
      params = filterConfig.toExternalQuery(filter);
    } else {
      // Default behavior if no toExternalQuery is provided
      params = getDefaultQueryParams(filter);
    }

    // Merge the params, allowing multiple values for the same key
    Object.entries(params).forEach(([key, value]) => {
      if (key in queryParams) {
        // Key already exists, need to handle multiple values
        const existing = queryParams[key];
        if (Array.isArray(existing)) {
          queryParams[key] = [...existing, value];
        } else {
          queryParams[key] = [existing, value];
        }
      } else {
        queryParams[key] = value;
      }
    });
  });

  return queryParams;
}

function getDefaultQueryParams(filter: AppliedFilterData): Record<string, any> {
  const field = filter.field || filter.category;

  switch (filter.comparer) {
    case 'is':
    case 'eq':
      return { [field]: filter.value };
    case 'not_is':
      return { [`${field}__not`]: filter.value };
    case 'contains':
      return { [`${field}__contains`]: filter.value };
    case 'not_contains':
      return { [`${field}__not_contains`]: filter.value };
    case 'starts':
      return { [`${field}__startswith`]: filter.value };
    case 'not_starts':
      return { [`${field}__not_startswith`]: filter.value };
    case 'any_of':
    case 'contains_multi':
      return { [`${field}__in`]: filter.value };
    case 'none_of':
      return { [`${field}__not_in`]: filter.value };
    case 'gt':
      return { [`${field}__gt`]: filter.value };
    case 'lt':
      return { [`${field}__lt`]: filter.value };
    case 'before':
      return { [`${field}__before`]: filter.value };
    case 'after':
      return { [`${field}__after`]: filter.value };
    case 'between':
      if (Array.isArray(filter.value) && filter.value.length === 2) {
        return {
          [`${field}__gte`]: filter.value[0],
          [`${field}__lte`]: filter.value[1],
        };
      }
      return {};
    case 'today':
      return { [`${field}__today`]: true };
    case 'yesterday':
      return { [`${field}__yesterday`]: true };
    default:
      // For unknown comparers, just pass through
      return { [`${field}__${filter.comparer}`]: filter.value };
  }
}
